import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faUser,
	faCalendarAlt,
	faGlobe,
	faMapMarkerAlt,
	faIdCard,
	faGraduationCap,
	faUsers,
	faSave,
	faTimes,
} from "@fortawesome/free-solid-svg-icons";

import Modal from "../../../../components/common/Modal";
import Button from "../../../../components/common/Button";
import TextInput from "../../../../components/common/TextInput";

import {
	useCreateStudentMutation,
	useUpdateStudentMutation,
} from "../../../../store/api/studentApiSlice";

import styles from "./StudentModal.module.css";

const StudentModal = ({ student, departments, parents, isOpen, onClose }) => {
	const [createStudent, { isLoading: isCreating }] = useCreateStudentMutation();
	const [updateStudent, { isLoading: isUpdating }] = useUpdateStudentMutation();

	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		dateOfBirth: "",
		country: "",
		placeOfBirth: "",
		citizenship: "",
		departmentId: "",
		parentId: "",
		isActive: true,
	});

	const [errors, setErrors] = useState({});
	const isEditing = !!student;
	const isLoading = isCreating || isUpdating;

	// Initialize form data
	useEffect(() => {
		if (student) {
			setFormData({
				firstName: student.firstName || "",
				lastName: student.lastName || "",
				dateOfBirth: student.dateOfBirth
					? new Date(student.dateOfBirth).toISOString().split("T")[0]
					: "",
				country: student.country || "",
				placeOfBirth: student.placeOfBirth || "",
				citizenship: student.citizenship || "",
				departmentId: student.departmentId || "",
				parentId: student.parentId || "",
				isActive: student.isActive ?? true,
			});
		} else {
			setFormData({
				firstName: "",
				lastName: "",
				dateOfBirth: "",
				country: "",
				placeOfBirth: "",
				citizenship: "",
				departmentId: "",
				parentId: "",
				isActive: true,
			});
		}
		setErrors({});
	}, [student, isOpen]);

	const handleInputChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		// Clear error when user starts typing
		if (errors[field]) {
			setErrors((prev) => ({
				...prev,
				[field]: "",
			}));
		}
	};

	const validateForm = () => {
		const newErrors = {};

		if (!formData.firstName.trim()) {
			newErrors.firstName = "First name is required";
		}

		if (!formData.lastName.trim()) {
			newErrors.lastName = "Last name is required";
		}

		if (!formData.dateOfBirth) {
			newErrors.dateOfBirth = "Date of birth is required";
		} else {
			const birthDate = new Date(formData.dateOfBirth);
			const today = new Date();
			if (birthDate >= today) {
				newErrors.dateOfBirth = "Date of birth must be in the past";
			}
		}

		if (!formData.country.trim()) {
			newErrors.country = "Country is required";
		}

		if (!formData.placeOfBirth.trim()) {
			newErrors.placeOfBirth = "Place of birth is required";
		}

		if (!formData.departmentId) {
			newErrors.departmentId = "Department is required";
		}

		if (!formData.parentId) {
			newErrors.parentId = "Parent is required";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSave = async () => {
		if (!validateForm()) return;

		try {
			const studentData = {
				...formData,
				dateOfBirth: new Date(formData.dateOfBirth).toISOString(),
			};

			if (isEditing) {
				await updateStudent({
					id: student.id,
					...studentData,
				}).unwrap();
			} else {
				await createStudent(studentData).unwrap();
			}

			onClose();
		} catch (error) {
			console.error("Failed to save student:", error);
			alert("Failed to save student. Please try again.");
		}
	};

	const getParentDisplayName = (parent) => {
		return `${parent.firstName} ${parent.lastName} (${parent.email})`;
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={
				<div className={styles.modalTitle}>
					<FontAwesomeIcon icon={faUser} />
					{isEditing
						? `Edit Student - ${student?.firstName} ${student?.lastName}`
						: "Add New Student"}
				</div>
			}
			size="large"
			footer={
				<div className={styles.modalFooter}>
					<Button variant="secondary" onClick={onClose} disabled={isLoading}>
						<FontAwesomeIcon icon={faTimes} />
						Cancel
					</Button>
					<Button variant="primary" onClick={handleSave} disabled={isLoading}>
						<FontAwesomeIcon icon={faSave} />
						{isLoading
							? "Saving..."
							: isEditing
							? "Update Student"
							: "Create Student"}
					</Button>
				</div>
			}
		>
			<div className={styles.modalContent}>
				<div className={styles.formGrid}>
					{/* Personal Information */}
					<div className={styles.formSection}>
						<h3 className={styles.sectionTitle}>
							<FontAwesomeIcon icon={faUser} />
							Personal Information
						</h3>

						<div className={styles.formRow}>
							<div className={styles.formGroup}>
								<label htmlFor="firstName">First Name *</label>
								<TextInput
									id="firstName"
									value={formData.firstName}
									onChange={(e) =>
										handleInputChange("firstName", e.target.value)
									}
									placeholder="Enter first name"
									error={errors.firstName}
									disabled={isLoading}
								/>
							</div>

							<div className={styles.formGroup}>
								<label htmlFor="lastName">Last Name *</label>
								<TextInput
									id="lastName"
									value={formData.lastName}
									onChange={(e) =>
										handleInputChange("lastName", e.target.value)
									}
									placeholder="Enter last name"
									error={errors.lastName}
									disabled={isLoading}
								/>
							</div>
						</div>

						<div className={styles.formRow}>
							<div className={styles.formGroup}>
								<label htmlFor="dateOfBirth">
									<FontAwesomeIcon icon={faCalendarAlt} />
									Date of Birth *
								</label>
								<input
									type="date"
									id="dateOfBirth"
									className={`${styles.dateInput} ${
										errors.dateOfBirth ? styles.error : ""
									}`}
									value={formData.dateOfBirth}
									onChange={(e) =>
										handleInputChange("dateOfBirth", e.target.value)
									}
									disabled={isLoading}
									max={new Date().toISOString().split("T")[0]}
								/>
								{errors.dateOfBirth && (
									<span className={styles.errorText}>{errors.dateOfBirth}</span>
								)}
							</div>

							<div className={styles.formGroup}>
								<label htmlFor="citizenship">
									<FontAwesomeIcon icon={faIdCard} />
									Citizenship
								</label>
								<TextInput
									id="citizenship"
									value={formData.citizenship}
									onChange={(e) =>
										handleInputChange("citizenship", e.target.value)
									}
									placeholder="Enter citizenship (optional)"
									disabled={isLoading}
								/>
							</div>
						</div>
					</div>

					{/* Location Information */}
					<div className={styles.formSection}>
						<h3 className={styles.sectionTitle}>
							<FontAwesomeIcon icon={faGlobe} />
							Location Information
						</h3>

						<div className={styles.formRow}>
							<div className={styles.formGroup}>
								<label htmlFor="country">
									<FontAwesomeIcon icon={faGlobe} />
									Country *
								</label>
								<TextInput
									id="country"
									value={formData.country}
									onChange={(e) => handleInputChange("country", e.target.value)}
									placeholder="Enter country"
									error={errors.country}
									disabled={isLoading}
								/>
							</div>

							<div className={styles.formGroup}>
								<label htmlFor="placeOfBirth">
									<FontAwesomeIcon icon={faMapMarkerAlt} />
									Place of Birth *
								</label>
								<TextInput
									id="placeOfBirth"
									value={formData.placeOfBirth}
									onChange={(e) =>
										handleInputChange("placeOfBirth", e.target.value)
									}
									placeholder="Enter place of birth"
									error={errors.placeOfBirth}
									disabled={isLoading}
								/>
							</div>
						</div>
					</div>

					{/* Academic Information */}
					<div className={styles.formSection}>
						<h3 className={styles.sectionTitle}>
							<FontAwesomeIcon icon={faGraduationCap} />
							Academic Information
						</h3>

						<div className={styles.formRow}>
							<div className={styles.formGroup}>
								<label htmlFor="departmentId">
									<FontAwesomeIcon icon={faGraduationCap} />
									Department *
								</label>
								<select
									id="departmentId"
									className={`${styles.select} ${
										errors.departmentId ? styles.error : ""
									}`}
									value={formData.departmentId}
									onChange={(e) =>
										handleInputChange("departmentId", e.target.value)
									}
									disabled={isLoading}
								>
									<option value="">Select a department</option>
									{departments.map((dept) => (
										<option key={dept.id} value={dept.id}>
											{dept.departmentName}
										</option>
									))}
								</select>
								{errors.departmentId && (
									<span className={styles.errorText}>
										{errors.departmentId}
									</span>
								)}
							</div>

							<div className={styles.formGroup}>
								<label htmlFor="parentId">
									<FontAwesomeIcon icon={faUsers} />
									Parent/Guardian *
								</label>
								<select
									id="parentId"
									className={`${styles.select} ${
										errors.parentId ? styles.error : ""
									}`}
									value={formData.parentId}
									onChange={(e) =>
										handleInputChange("parentId", e.target.value)
									}
									disabled={isLoading}
								>
									<option value="">Select a parent/guardian</option>
									{parents.map((parent) => (
										<option key={parent.id} value={parent.id}>
											{getParentDisplayName(parent)}
										</option>
									))}
								</select>
								{errors.parentId && (
									<span className={styles.errorText}>{errors.parentId}</span>
								)}
							</div>
						</div>
					</div>

					{/* Status */}
					{isEditing && (
						<div className={styles.formSection}>
							<h3 className={styles.sectionTitle}>Status</h3>
							<div className={styles.checkboxGroup}>
								<label className={styles.checkboxLabel}>
									<input
										type="checkbox"
										checked={formData.isActive}
										onChange={(e) =>
											handleInputChange("isActive", e.target.checked)
										}
										disabled={isLoading}
									/>
									<span className={styles.checkboxText}>Active Student</span>
								</label>
							</div>
						</div>
					)}
				</div>
			</div>
		</Modal>
	);
};

export default StudentModal;
